import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';

const BlogHomeSafetyPage: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const [heroImage, setHeroImage] = useState<string>('');

  useEffect(() => {
    // Fetch the hero image
    fetch('http://localhost:4000/api/images')
      .then(res => res.json())
      .then(data => {
        const image = data.images.find((img: any) => 
          img.filename === 'FD-circle-3-text-box (1).jpg'
        );
        if (image) {
          setHeroImage(`${API_URL}/api/images/${image.id}`);
        }
      })
      .catch(err => console.error('Error fetching image:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="blog-post-page">
      {/* Hero Section */}
      <section className="blog-hero-section">
        <div className="blog-hero-content">
          <div className="blog-story-image-wrapper">
            {heroImage ? (
              <img src={heroImage} alt="Multi-generational family" className="blog-story-image" />
            ) : (
              <div style={{ width: '100%', height: '300px', background: '#f0f0f0' }}></div>
            )}
          </div>
          <h1 className="blog-hero-title">Rethinking Home Safety for Aging Parents</h1>
          <div className="blog-meta">
            <span className="blog-author">By Arar Han</span>
            <span className="blog-date">June 08, 2025</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="blog-content-wrapper">
        <div className="blog-content-container">

          {/* Introduction */}
          <div className="blog-content-section">
            <p>
                Maybe it's because I have a toddler at home right now, but I think about, anticipate, and make great efforts to actively avoid falls. Proactively strategizing 
                for my family led to my thinking that we handle senior falls wrong. In fact, I don't think we handle them at all, even though they become more and more 
                likely to occur throughout the aging process.
              </p>
              <p>
                According to the CDC, falls are the number one injury among aging adults 65 and over, and 25-30% of seniors fall every year. The NOAA adds that falling is 
                both the number one fatal injury and non fatal injury among senior citizens. With odds this significant, why don't we do more to prevent senior falls?
              </p>
              <p>
                When the first of my three children started to walk, falling was not only expected, but something I anxiously sought to avoid or soften. I would go around our house on hands and knees, looking for any sharp 
                corners that might injure my children as they fell. Edges posed a threat as well; so any hard edges were duly covered in strips of foam 
                and double stick tape. A glass coffee table with both sharp corners and edges went on loan to my sister's place. We were not taking 
                any chances when it came to the safety of our beloveds.
              </p>
              <p>
                That we go to such lengths to protect our children but not our parents or grandparents is sad. While they are adults with agency and 
                self-conscious pride, there are precautions that can be undertaken to reduce the incidence and severity of elderly falls. Half of all falls 
                happen inside the home, and half of the rest happen near home. So, much of the focus should be in and around the home.
              </p>
              <p>
                So here are four concrete steps that those who have a senior they care about can focus on to improve their loved one's safety and 
                longevity. I thought of them while visiting two nonagenarians my husband and I care about, and they are based on observed issues in 
                their actual homes.
              </p>
          </div>

          {/* Four Concrete Steps */}
          <div className="blog-content-section">
            <p>
              <strong>First,</strong> any slippery places like the shower or bath can be equipped with non-slip strips and grab bars. Frankly, all showers in all homes 
              should have these safety features. My own shower slips are like little skids in the rain, and they haven't ended in total falls. But as 
              muscle strength and control decline, I understand how these skids will progress. Taking the time to reduce the odds of falling by 
              adding a few simple safety features is a no brainer. We should all do it.
            </p>

            <p>
              <strong>Second,</strong> we should reduce seniors' exposure to stairs and surfaces with abrupt grade changes. Falls happen more often when there is 
              something to trip on. Flat surface homes make sense for all, especially those prone to falling. And just as even a single step poses a 
              problem for my son who likes to zip around on his wheely bus, they are also a challenge for walkers and wheelchairs. Let's help our 
              loved ones get away from steps as much as we can, even so far as to move homes if they're able.
            </p>

            <p>
              <strong>Third,</strong> we should encourage seniors to use walking sticks to foster a closer awareness of the ground immediately in front of them as 
              they walk. This is mostly for the falls that happen away from home. A walking stick or cane, if more appropriate, makes us pay closer 
              attention to the ground in front of us, and notice the uneven and less safe parts so we can avoid them or proceed with caution.
            </p>

            <p>
              <strong>Fourth and finally,</strong> we have to help seniors clean up any clutter around the house on a regular basis. Particularly if they have lived in a 
              place for a long time, they may not have examined their living spaces, looking for lurking dangers. They may discount the edge of the 
              rug that they have tripped on a few times, or the awkward bend around the breakfast table to get to the patio door which forces an 
              off-balance step or two. Just like my little skids in the shower, they portend potentially tougher consequences to come. They should 
              be eliminated when possible.
            </p>

            <p>
              Do you have senior citizens you love and want to protect? I do too. I encourage you to broach this topic with them or their caregivers, 
              and encourage prompt action on these safety measures. We can lovingly advocate to "fall proof" our aging loved ones' homes as we 
              and they grow older.
            </p>
          </div>

          {/* Medical Disclaimer and Categories Section */}
          <div className="blog-disclaimer-section">
            <p className="blog-disclaimer">
              <strong>Medical Disclaimer:</strong> This article is for informational purposes only. Always consult with healthcare professionals, occupational 
              therapists, or certified aging-in-place specialists for personalized home safety recommendations.
            </p>

            <p className="blog-last-updated">Last updated: June 08, 2025</p>

            <div className="blog-categories-box">
              <h3>Alert1 Blog Categories</h3>
              <div className="blog-categories-grid">
                <div className="blog-category-column">
                  <a href="/blog" className="blog-category-link">Alert1 Blog</a>
                  <a href="/blog" className="blog-category-link">Family Caregivers</a>
                  <a href="/blog" className="blog-category-link">Resources</a>
                </div>
                <div className="blog-category-column">
                  <a href="/blog" className="blog-category-link">Elder Care Planning</a>
                  <a href="/blog" className="blog-category-link">General</a>
                  <a href="/blog" className="blog-category-link">Senior Safety & Independence</a>
                </div>
                <div className="blog-category-column">
                  <a href="/blog" className="blog-category-link">Fall Prevention</a>
                  <a href="/blog" className="blog-category-link">Recipes</a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BlogHomeSafetyPage;

